import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { AudioBus } from '../../audio/AudioBus';
import type { ThemeAssetEntry } from '../../theme';
import { woodGradient } from '../board/BoardPanel';
import { paintedPlate } from '../board/BrassButton';
import { animate, displayStyle, ease } from '../board/ui';
import { glowTexture } from '../road/glow';
import { NoticeStack } from './NoticeStack';
import { OverlayStage, type OverlayHandle } from './OverlayStage';

/** How long one toast is up. */
const LIFE_MS = 1500;
/** The plate's height, and the room it takes in the notices' column (it
 *  pops to 110%). */
const PLATE_H = 50;
const ROOM = 56;

const GEM = 'shared:icon:gem';

/** The toasts on screen as plain data, for browser tests. */
export interface GemToastSnapshot {
  toasts: string[];
}

/**
 * "+N" and a gem whenever the player's gems go up (producer: "I have no
 * idea when I'm being awarded gems"), with a bright pickup sound: a gem on
 * the kit's painted wooden plate pops up top centre, under the wallet, and
 * fades. Each takes its place in the game's column of notices
 * ({@link NoticeStack}), under any already showing, so none overlap.
 * Drawn on the overlay canvas over any screen; it takes no taps.
 */
export class GemToastLayer extends Container {
  private w = 1;
  private live = 0;

  layout(width: number): void {
    this.w = width;
    for (const toast of this.children) toast.x = Math.round(width / 2);
  }

  get count(): number {
    return this.live;
  }

  get snapshot(): GemToastSnapshot {
    return {
      toasts: this.children.map((c) => (c.getChildByLabel('gem-toast-text') as Text | null)?.text ?? ''),
    };
  }

  /** Pop up "+amount"; resolves when it has gone. */
  async show(amount: number, gem: Texture | null): Promise<void> {
    this.live += 1;
    const toast = this.build(amount, gem);
    toast.x = Math.round(this.w / 2);
    this.addChild(toast);
    const slot = NoticeStack.shared.add(
      ROOM,
      ({ y, shown }) => {
        if (toast.destroyed) return;
        toast.y = Math.round(y + ROOM / 2);
        toast.visible = shown;
      },
      () => {
        const plate = toast.destroyed || !toast.visible ? null : toast.getChildByLabel('gem-toast-plate');
        if (!plate) return null;
        const b = plate.getBounds();
        return { x: b.x, y: b.y, width: b.width, height: b.height };
      },
    );
    await animate(LIFE_MS, (t) => {
      // In with a pop (no bigger than its room), out with a fade.
      const scale = t < 0.12 ? 0.6 + 0.5 * ease.outBack(t / 0.12) : t < 0.25 ? 1.1 - 0.1 * ((t - 0.12) / 0.13) : 1;
      toast.scale.set(Math.min(1.1, scale));
      toast.alpha = t < 0.12 ? t / 0.12 : t > 0.75 ? 1 - (t - 0.75) / 0.25 : 1;
    });
    slot.release();
    if (!toast.destroyed) toast.destroy({ children: true });
    this.live -= 1;
  }

  private build(amount: number, gem: Texture | null): Container {
    const toast = new Container();
    const text = new Text({ text: `+${amount}`, style: displayStyle(28) });
    text.label = 'gem-toast-text';
    text.anchor.set(0, 0.5);
    const iconSize = 30;
    const gap = 8;
    const h = PLATE_H;
    const w = Math.round(text.width + gap + iconSize + 52);
    const glow = new Sprite(glowTexture());
    glow.anchor.set(0.5);
    glow.tint = 0x8fd8ff;
    // A soft blue light behind the plate, kept within its room.
    glow.alpha = 0.6;
    glow.width = w * 1.35;
    glow.height = ROOM * 1.2;
    toast.addChild(glow);
    const plate = paintedPlate('wood', w, h);
    if (plate) {
      plate.position.set(-w / 2, -h / 2);
      plate.label = 'gem-toast-plate';
      toast.addChild(plate);
    } else {
      toast.addChild(
        Object.assign(new Graphics(), { label: 'gem-toast-plate' })
          .roundRect(-w / 2, -h / 2 + 3, w, h, 14)
          .fill({ color: 0x000000, alpha: 0.4 })
          .roundRect(-w / 2, -h / 2, w, h, 14)
          .fill(woodGradient())
          .stroke({ color: 0xd9a441, width: 2 }),
      );
    }
    const left = -(text.width + gap + iconSize) / 2;
    text.position.set(left, 1);
    toast.addChild(text);
    if (gem) {
      const icon = new Sprite(gem);
      icon.anchor.set(0, 0.5);
      icon.width = icon.height = iconSize;
      icon.position.set(left + text.width + gap, 0);
      toast.addChild(icon);
    }
    return toast;
  }
}

declare global {
  interface Window {
    /** The gem toasts on screen, for browser tests. */
    __gemToast?: { ui: () => GemToastSnapshot };
  }
}

let layer: GemToastLayer | null = null;
let handle: OverlayHandle | null = null;
/** Toasts asked for and not yet gone: the layer leaves with the last. */
let pending = 0;

/**
 * Pop up a gem toast over whatever is on screen (the overlay canvas), with
 * the pickup sound. `art` names the gem icon and the painted plates by URL.
 */
export async function showGemToast(
  amount: number,
  art: { gem: string; plates?: Record<string, ThemeAssetEntry> },
): Promise<void> {
  if (amount <= 0) return;
  pending += 1;
  try {
    AudioBus.shared.play('bonus:claim', { volume: 0.7 });
    const loader = AssetLoader.shared;
    await Promise.all([
      loader.loadTextures([{ alias: GEM, src: art.gem }]).catch(() => undefined),
      art.plates ? loader.loadAssets(art.plates).catch(() => undefined) : undefined,
    ]);
    if (!layer || !handle) {
      const piece = new GemToastLayer();
      piece.eventMode = 'none';
      layer = piece;
      handle = OverlayStage.shared.show(piece, { layer: 30 });
      window.__gemToast = { ui: () => piece.snapshot };
    }
    const current = layer;
    if (await handle.shown) await current.show(amount, loader.getTexture(GEM));
  } finally {
    pending -= 1;
    if (pending === 0 && layer) {
      handle?.remove();
      layer.destroy({ children: true });
      layer = null;
      handle = null;
      delete window.__gemToast;
    }
  }
}
